import React, { useState } from 'react';
import { Link, useForm, router } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import useRoutes from '@/hooks/useRoutes';
import { Academy, User } from '@/types';
import { AlertCircle, Trash2 } from 'lucide-react';

interface EditProps {
    user: User;
    academies: Academy[];
    errors?: Record<string, string>;
}

const EditContent: React.FC<EditProps> = ({
    user,
    academies,
    errors: serverErrors,
}) => {
    const { route } = useRoutes();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const { data, setData, post, processing, errors } = useForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        academy_id: user.current_academy?.id || '',
        is_active: user.is_active,
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        post(route('staff-commercial-users-edit', { user_id: user.id }).toString());
    };

    const handleDelete = () => {
        router.post(route('staff-commercial-users-delete', { user_id: user.id }).toString(), {}, {
            onSuccess: () => {
                router.visit(route('staff-commercial-users').toString());
            },
        });
    };

    const allErrors = { ...serverErrors, ...errors };

    return (
        <StaffLayout>
            <div className="mx-auto max-w-3xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">
                        Editar Usuario Comercial
                    </h1>
                    <p className="mt-2 text-gray-600">
                        Modifica la información del usuario
                    </p>
                </div>

                <div className="rounded-lg bg-white p-6 shadow">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Email (read-only) */}
                        <div>
                            <label
                                htmlFor="email"
                                className="block text-sm font-medium text-gray-700"
                            >
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={user.email}
                                disabled
                                className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-500 shadow-sm sm:text-sm"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                El email no puede ser modificado
                            </p>
                        </div>

                        {/* First Name */}
                        <div>
                            <label
                                htmlFor="first_name"
                                className="block text-sm font-medium text-gray-700"
                            >
                                Nombre
                            </label>
                            <input
                                type="text"
                                id="first_name"
                                value={data.first_name}
                                onChange={(e) =>
                                    setData('first_name', e.target.value)
                                }
                                className={`mt-1 block w-full rounded-md border ${
                                    allErrors.first_name
                                        ? 'border-red-300'
                                        : 'border-gray-300'
                                } px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                            />
                            {allErrors.first_name && (
                                <div className="mt-2 flex items-center text-sm text-red-600">
                                    <AlertCircle size={16} className="mr-1" />
                                    {allErrors.first_name}
                                </div>
                            )}
                        </div>

                        {/* Last Name */}
                        <div>
                            <label
                                htmlFor="last_name"
                                className="block text-sm font-medium text-gray-700"
                            >
                                Apellido
                            </label>
                            <input
                                type="text"
                                id="last_name"
                                value={data.last_name}
                                onChange={(e) =>
                                    setData('last_name', e.target.value)
                                }
                                className={`mt-1 block w-full rounded-md border ${
                                    allErrors.last_name
                                        ? 'border-red-300'
                                        : 'border-gray-300'
                                } px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                            />
                            {allErrors.last_name && (
                                <div className="mt-2 flex items-center text-sm text-red-600">
                                    <AlertCircle size={16} className="mr-1" />
                                    {allErrors.last_name}
                                </div>
                            )}
                        </div>

                        {/* Academy */}
                        <div>
                            <label
                                htmlFor="academy_id"
                                className="block text-sm font-medium text-gray-700"
                            >
                                Academia
                            </label>
                            <select
                                id="academy_id"
                                value={data.academy_id}
                                onChange={(e) =>
                                    setData('academy_id', Number(e.target.value))
                                }
                                className={`mt-1 block w-full rounded-md border ${
                                    allErrors.academy_id
                                        ? 'border-red-300'
                                        : 'border-gray-300'
                                } px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm`}
                            >
                                <option value="">Selecciona una academia</option>
                                {academies.map((academy) => (
                                    <option key={academy.id} value={academy.id}>
                                        {academy.name}
                                    </option>
                                ))}
                            </select>
                            {allErrors.academy_id && (
                                <div className="mt-2 flex items-center text-sm text-red-600">
                                    <AlertCircle size={16} className="mr-1" />
                                    {allErrors.academy_id}
                                </div>
                            )}
                        </div>

                        {/* Is Active */}
                        <div>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={data.is_active}
                                    onChange={(e) =>
                                        setData('is_active', e.target.checked)
                                    }
                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="ml-2 text-sm font-medium text-gray-700">
                                    Usuario activo
                                </span>
                            </label>
                            <p className="ml-6 mt-1 text-xs text-gray-500">
                                Los usuarios inactivos no podrán iniciar sesión
                            </p>
                        </div>

                        {/* General Error */}
                        {allErrors.general && (
                            <div className="rounded-md bg-red-50 p-4">
                                <div className="flex">
                                    <AlertCircle
                                        size={20}
                                        className="mr-3 text-red-600"
                                    />
                                    <div className="text-sm text-red-700">
                                        {allErrors.general}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between border-t pt-4">
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(true)}
                                className="inline-flex items-center rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                            >
                                <Trash2 size={16} className="mr-2" />
                                Desactivar Usuario
                            </button>

                            <div className="flex space-x-3">
                                <Link href={route('staff-commercial-users').toString()}>
                                    <SecondaryButton type="button">
                                        Cancelar
                                    </SecondaryButton>
                                </Link>
                                <PrimaryButton
                                    type="submit"
                                    disabled={processing}
                                >
                                    {processing
                                        ? 'Guardando...'
                                        : 'Guardar Cambios'}
                                </PrimaryButton>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="mx-4 max-w-md rounded-lg bg-white p-6">
                        <h3 className="mb-4 text-lg font-semibold text-gray-900">
                            ¿Desactivar usuario?
                        </h3>
                        <p className="mb-6 text-sm text-gray-600">
                            Esta acción desactivará al usuario{' '}
                            <strong>{user.full_name}</strong>. El usuario no
                            podrá iniciar sesión pero sus datos se conservarán.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <SecondaryButton
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                Cancelar
                            </SecondaryButton>
                            <button
                                onClick={handleDelete}
                                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                            >
                                Desactivar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </StaffLayout>
    );
};

const Edit: React.FC<EditProps> = (props) => {
    return (
        <ThemeWrapper>
            <EditContent {...props} />
        </ThemeWrapper>
    );
};

export default Edit;
